import { PAGES } from "./pageRegistry";
import { isCustomWebsitePageKey } from "./customWebsitePages";
import { isNavOmitted } from "./websiteNavOmit";
import type { PageTweaks, Tweaks } from "./types";

export { isNavOmitted } from "./websiteNavOmit";

export const WEBSITE_NAV_PAGE_KEYS = [
  "home",
  "shop",
  "journal",
  "about",
  "contact",
  "content",
  "search",
] as const;

/** Same default sequence as `parseNavOrder("")` / Site Header nav. */
export const WEBSITE_PAGES_RAIL_NAV_ORDER_FALLBACK = [
  "home",
  "search",
  "shop",
  "blog",
  "about",
  "contact",
  "content",
  "linkInBio",
] as const;

/**
 * Site Header keys that can appear as text links in the live nav.
 * Search stays a header icon (not a text link), so it is omitted here.
 */
const HEADER_NAV_KEYS = [
  "home",
  "shop",
  "blog",
  "about",
  "contact",
  "content",
  "linkInBio",
] as const;

function pageKeyFromNavKey(navKey: string): string {
  if (navKey === "blog") return "journal";
  return navKey;
}

/** Blog / journal public-nav keys. */
export function isBlogNavigationKey(key: string): boolean {
  const navKey = String(key || "").trim();
  return navKey === "blog" || navKey === "journal" || navKey === "journalPost";
}

/**
 * When true, Blog is omitted from the site header, Pages rail, and pages drawer
 * for every creator — including existing sites with no builder snapshot.
 * Publish/generate still gate separately via Blog Stripe plan / credits.
 */
export const BLOG_NAV_LOCKED = true;

export const BLOG_NAV_DISABLED_TIP =
  "Blog isn’t available in site navigation yet.";

/** Prefer this over hard-coding `isBlogNavigationKey` disables. */
export function isBlogNavChromeLocked(key: string): boolean {
  return BLOG_NAV_LOCKED && isBlogNavigationKey(key);
}

/**
 * Always listed under “Not on website” in the pages dropdown / rail
 * (Navigation Dropdown.html). Selecting one shows a not-available toast.
 */
export const WEBSITE_UNLINKED_PAGE_KEYS = [
  "mediaKit",
  "brandPartners",
] as const;

export const PLACEHOLDER_PAGE_KEYS = new Set<string>([
  ...WEBSITE_UNLINKED_PAGE_KEYS,
]);

export const PLACEHOLDER_PAGE_LABELS: Record<string, string> = {
  mediaKit: "Media Kit",
  brandPartners: "Brand Partners",
};

export const WEBSITE_PAGES_SECTION_LABEL = "Website pages";
export const NOT_ON_WEBSITE_SECTION_LABEL = "Not on website";

export const NOT_LINKED_TIP =
  "These pages are live and shareable by link, but they don't appear in your site navigation.";

/**
 * Pages omitted from site nav + placeholder Media Kit / Brand Partners.
 * Order: omitted built-ins, unlinked customs, then design placeholders.
 */
export function websiteUnlinkedPageKeys(args: {
  navOmit?: Iterable<string> | null;
  customPages?: Array<{ id: string; inNav?: boolean }>;
  enabledPageKeys?: Set<string> | null;
}): string[] {
  const omit = new Set(
    Array.from(args.navOmit || []).map((k) =>
      k === "blog" ? "journal" : String(k || "").trim(),
    ),
  );
  const enabled = args.enabledPageKeys ?? null;
  const omittedNav = WEBSITE_NAV_PAGE_KEYS.filter((key) => {
    if (!isWebsiteEditorPageEnabled(key, enabled)) return false;
    return omit.has(key) || omit.has(key === "journal" ? "blog" : key);
  });
  const unlinkedCustom = (args.customPages || [])
    .filter((p) => !p.inNav || omit.has(p.id))
    .map((p) => p.id);
  const out: string[] = [];
  const seen = new Set<string>();
  for (const key of [
    ...omittedNav,
    ...unlinkedCustom,
    ...WEBSITE_UNLINKED_PAGE_KEYS,
  ]) {
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out;
}

export const LINK_IN_BIO_TIP =
  "Your link-in-bio is shared from your social bios — it isn't part of your website navigation.";

export const WEBSITE_LOCK_TIP = "Your website is included on the $97 plan.";

function humanizeEditorPageKey(key: string): string {
  const stripped = key.replace(/^c_/, "").replace(/-/g, " ").trim();
  if (!stripped) return key;
  return stripped
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function websiteEditorPageLabel(key: string): string {
  if (key === "journalPost") return PAGES.journal?.label || "Blog";
  if (key === "journal") return PAGES.journal?.label || "Blog";
  if (key === "linkInBio") return "Link in Bio";
  return (
    PLACEHOLDER_PAGE_LABELS[key] ||
    PAGES[key]?.label ||
    humanizeEditorPageKey(key)
  );
}

export function isWebsiteEditorPageActive(
  key: string,
  currentPage: string,
): boolean {
  if (key === currentPage) return true;
  return key === "journal" && currentPage === "journalPost";
}

export function isWebsitePageLocked(
  key: string,
  websiteLocked: boolean,
): boolean {
  if (!websiteLocked) return false;
  return key !== "linkInBio";
}

/**
 * Page keys the creator actually opted into during Create your website.
 * `null` means no builder snapshot (older sites) — show the full rail.
 * Only strict `true` counts so stale / weird truthy values never unlock a page.
 */
export function enabledWebsiteEditorPageKeysFromBuilder(
  builder: unknown,
): Set<string> | null {
  if (!builder || typeof builder !== "object") return null;
  const pages = (builder as { pages?: Record<string, unknown> }).pages;
  if (!pages || typeof pages !== "object") return null;
  const linkinbio = (builder as { linkinbio?: unknown }).linkinbio;
  const keys = new Set<string>(["home"]);
  if (pages.shop === true) keys.add("shop");
  if (pages.blog === true || pages.journal === true) keys.add("journal");
  if (pages.recent === true) keys.add("content");
  if (pages.about === true) keys.add("about");
  if (pages.contact === true) keys.add("contact");
  if (pages.search === true) keys.add("search");
  if (linkinbio === true) keys.add("linkInBio");
  return keys;
}

export function isWebsiteEditorPageEnabled(
  pageKey: string,
  enabled: Set<string> | null,
): boolean {
  // Not a Create-your-website page. Highlights open Content — never list this.
  if (pageKey === "platform") return false;
  // Hard-hide Blog/Journal from Pages rail + drawers while nav chrome is locked
  // (covers existing creators where `enabled` is null → full rail).
  if (isBlogNavChromeLocked(pageKey)) return false;
  if (!enabled) return true;
  if (isCustomWebsitePageKey(pageKey)) return true;
  if (pageKey === "journalPost") return enabled.has("journal");
  return enabled.has(pageKey);
}

/**
 * Builder / site-header nav can diverge: creators may re-add a page to the live
 * header without the builder snapshot flipping on. Treat any header-visible page
 * as enabled so the Pages rail matches the canvas nav.
 */
export function applyNavOmitToEnabledPageKeys(
  enabled: Set<string> | null,
  navOmit: Iterable<string> | null | undefined,
): Set<string> | null {
  if (!enabled) return null;
  const omit = new Set(Array.from(navOmit || []));
  const next = new Set(enabled);
  // Header keys only — Search is builder-gated and is not a SITE_HEADER item.
  for (const navKey of HEADER_NAV_KEYS) {
    if (isBlogNavChromeLocked(navKey)) continue;
    if (isNavOmitted(navKey, omit)) continue;
    next.add(pageKeyFromNavKey(navKey));
  }
  // Builder may still list journal; drop it while Blog nav chrome is locked.
  if (BLOG_NAV_LOCKED) {
    next.delete("journal");
    next.delete("blog");
    next.delete("journalPost");
  }
  return next;
}

/**
 * Linked Pages-rail / mobile sheet order — same sequence as Site Header
 * navigation (`nav.order`), with `blog` → `journal`. Link in Bio is excluded
 * (shown in its own group). Search follows `nav.order` like other pages
 * (`parseNavOrder` already merges missing built-in keys).
 */
export function orderedLinkedWebsitePageKeys(args: {
  navOrder: readonly string[];
  navOmit?: Iterable<string> | null;
  customPages?: Array<{ id: string; inNav?: boolean }>;
  enabledPageKeys?: Set<string> | null;
}): string[] {
  const omit = new Set(Array.from(args.navOmit || []));
  const customs = args.customPages || [];
  const enabled = args.enabledPageKeys ?? null;
  const out: string[] = [];
  const seen = new Set<string>();

  const push = (pageKey: string) => {
    if (!pageKey || seen.has(pageKey)) return;
    if (pageKey === "linkInBio") return;
    // Drop Blog even if still present in stored nav.order (existing creators).
    if (isBlogNavChromeLocked(pageKey)) return;
    if (isNavOmitted(pageKey, omit)) return;
    if (!isWebsiteEditorPageEnabled(pageKey, enabled)) return;
    if (isCustomWebsitePageKey(pageKey)) {
      const row = customs.find((p) => p.id === pageKey);
      if (!row?.inNav) return;
    }
    seen.add(pageKey);
    out.push(pageKey);
  };

  for (const navKey of args.navOrder) {
    push(pageKeyFromNavKey(String(navKey || "").trim()));
  }
  for (const p of customs) {
    if (p.inNav) push(p.id);
  }

  return out;
}

/**
 * Pages list in Site Header order. Link in Bio stays after the website pages
 * in the default order. A page the user dragged to the end of the header
 * (Search or any other) is rendered after Link in Bio so it stays last.
 */
export function websitePagesRailKeys(args: {
  navOrder: readonly string[];
  navOmit?: Iterable<string> | null;
  customPages?: Array<{ id: string; inNav?: boolean }>;
  enabledPageKeys?: Set<string> | null;
}): string[] {
  const linked = orderedLinkedWebsitePageKeys(args);
  if (!isWebsiteEditorPageEnabled("linkInBio", args.enabledPageKeys ?? null)) {
    return linked;
  }
  const omit = new Set(args.navOmit || []);
  const canonicalLast = WEBSITE_PAGES_RAIL_NAV_ORDER_FALLBACK.map((key) =>
    pageKeyFromNavKey(key),
  )
    .filter((key) => key !== "linkInBio" && !isNavOmitted(key, omit))
    .at(-1);
  const last = linked[linked.length - 1];
  if (!last || linked.length < 2 || last === canonicalLast) {
    return [...linked, "linkInBio"];
  }
  return [...linked.slice(0, -1), "linkInBio", last];
}

export function websitePagesRailNavOrder(
  navOrder: readonly string[] | null | undefined,
): readonly string[] {
  return navOrder?.length ? navOrder : WEBSITE_PAGES_RAIL_NAV_ORDER_FALLBACK;
}

/** Map Site Header nav key → site-builder `pages.*` id (when applicable). */
export function builderPageIdForNavKey(
  navKey: string,
): "shop" | "blog" | "recent" | "about" | "contact" | null {
  switch (navKey) {
    case "shop":
      return "shop";
    case "blog":
      return "blog";
    case "content":
      return "recent";
    case "about":
      return "about";
    case "contact":
      return "contact";
    default:
      return null;
  }
}

/**
 * Site builder turns unused pages off by hiding every standard section.
 * Re-adding the page to nav must clear those hides so PageHeader / grids show.
 * Only clears when *all* standard sections are hidden (builder-off), so a
 * creator who hid a single section keeps that choice.
 */
export function revealStandardPageSections(
  page: PageTweaks | undefined,
  pageKey: string,
): PageTweaks | null {
  const def = PAGES[pageKey];
  if (!def) return null;
  const cur = page || {};
  const hidden = { ...(cur.hidden || {}) };
  const standard = def.sections.filter((s) => !s.defaultHidden);
  if (standard.length === 0) return null;
  const allHidden = standard.every((s) => hidden[s.id] === true);
  if (!allHidden) return null;
  let changed = false;
  for (const s of standard) {
    if (hidden[s.id] === true) {
      delete hidden[s.id];
      changed = true;
    }
  }
  if (!changed) return null;
  return { ...cur, hidden };
}

/**
 * When a creator adds a page back to the header, also unlock it in the Pages
 * rail (builder snapshot) and unhide its canvas sections.
 */
export function enableWebsitePageFromNav(
  tweaks: Tweaks,
  navKey: string,
): Tweaks | null {
  const pageKey = pageKeyFromNavKey(navKey);
  let next: Tweaks = tweaks;
  let changed = false;

  if (pageKey !== "linkInBio" && pageKey !== "home" && PAGES[pageKey]) {
    const revealed = revealStandardPageSections(
      tweaks.pages?.[pageKey],
      pageKey,
    );
    if (revealed) {
      next = {
        ...next,
        pages: { ...(next.pages || {}), [pageKey]: revealed },
      };
      changed = true;
    }
  }

  const builderId = builderPageIdForNavKey(navKey);
  const builder = next.gettingStarted?.builder;
  if (builderId && builder && typeof builder === "object") {
    const pages = {
      ...((builder as { pages?: Record<string, unknown> }).pages || {}),
    };
    if (pages[builderId] !== true) {
      pages[builderId] = true;
      next = {
        ...next,
        gettingStarted: {
          ...(next.gettingStarted || {}),
          builder: { ...builder, pages },
        },
      };
      changed = true;
    }
  } else if (navKey === "linkInBio" && builder && typeof builder === "object") {
    if ((builder as { linkinbio?: unknown }).linkinbio !== true) {
      next = {
        ...next,
        gettingStarted: {
          ...(next.gettingStarted || {}),
          builder: { ...builder, linkinbio: true },
        },
      };
      changed = true;
    }
  }

  return changed ? next : null;
}

/**
 * Heal pages that are live in the header but still have builder-forced
 * section hides (blank Blog / About / etc. after re-adding to nav).
 */
export function healNavVisiblePageSections(
  tweaks: Tweaks,
  navOmit: Iterable<string> | null | undefined,
): Tweaks | null {
  const omit = new Set(Array.from(navOmit || []));
  let next: Tweaks = tweaks;
  let changed = false;

  let nextPages = tweaks.pages ? { ...tweaks.pages } : undefined;
  for (const navKey of HEADER_NAV_KEYS) {
    if (isNavOmitted(navKey, omit)) continue;
    const pageKey = pageKeyFromNavKey(navKey);
    if (!PAGES[pageKey]) continue;
    const revealed = revealStandardPageSections(nextPages?.[pageKey], pageKey);
    if (!revealed) continue;
    if (!nextPages) nextPages = {};
    nextPages[pageKey] = revealed;
    changed = true;
  }
  if (nextPages && changed) {
    next = { ...next, pages: nextPages };
  }

  const builder = next.gettingStarted?.builder;
  if (builder && typeof builder === "object") {
    let nextBuilder = builder as Record<string, unknown>;
    let builderChanged = false;
    const pages = {
      ...((nextBuilder.pages as Record<string, unknown>) || {}),
    };
    for (const navKey of HEADER_NAV_KEYS) {
      if (isNavOmitted(navKey, omit)) continue;
      if (navKey === "linkInBio") {
        if (nextBuilder.linkinbio !== true) {
          nextBuilder = { ...nextBuilder, linkinbio: true };
          builderChanged = true;
        }
        continue;
      }
      const builderId = builderPageIdForNavKey(navKey);
      if (!builderId) continue;
      if (pages[builderId] !== true) {
        pages[builderId] = true;
        builderChanged = true;
      }
    }
    if (builderChanged) {
      next = {
        ...next,
        gettingStarted: {
          ...(next.gettingStarted || {}),
          builder: { ...nextBuilder, pages },
        },
      };
      changed = true;
    }
  }

  return changed ? next : null;
}
